<template>
  <div class="container">
    <div class="year-banner">
      <p>
        <label for="year-select">Data Year:</label>
        <select
          id="year-select"
          class="hcps-year-select"
          v-model="dataYear"
        >
          <option>2014</option>
          <option>2022</option>
        </select>
      </p>
      <p class="year-description">
        Changing the year will clear the selected questions and filters.
      </p>
    </div>

    <div class="options">
      <div class="topics">
        <sidebar-component />
      </div>

      <div class="hcps-parameters">
        <div
          v-if="!isLoadingYear && chartData != null && chartData.data.length > 0"
          class="topbar clip-required"
        >
          <div class="row">
            <div class="col-12 col-sm-6 col-md-3 topbar-option">
              <filterGrid
                :only="['showPercentage']"
                :label-position="'vertical'"
                :clear-button="false"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3 topbar-option">
              <filterGrid
                :only="['confidenceInterval']"
                :label-position="'vertical'"
                :clear-button="false"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3 topbar-option">
              <filterGrid
                :only="['showGraph']"
                :label-position="'vertical'"
                :clear-button="false"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3 topbar-option harness-ui-filtergrid-row">
              <fieldset>
                <legend class="col-form-label harness-ui-radiogroup-legend">
                  Download:
                </legend>
                <div class="topbar-option-value">
                  <button
                    aria-label="Download chart as PNG"
                    class="download-button"
                    @click="exportToPNG"
                  >
                    Chart <span class="badge">PNG</span>
                  </button>
                </div>
                <div class="topbar-option-value">
                  <button
                    aria-label="Download table as CSV"
                    class="download-button"
                    @click="exportToCSV"
                  >
                    Table <span class="badge">CSV</span>
                  </button>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div id="dashboard-chart-container">
          <template v-if="isLoadingYear || chartData == null || chartData.data.length === 0">
            <div class="alert-message alert-initial-state">
              <p>
                Select a Primary Question to get started.
              </p>
              <p>
                After you select a Primary Question, a chart or table will appear in
                this space. The chart or table will be updated as you make additional
                selections.
              </p>
            </div>
          </template>
          <template v-else-if="isChartNoResults()">
            <div class="alert-message alert-no-results">
              <p>
                No survey responses are available for the selected analysis. As a
                result, no results can be produced.
              </p>
              <p>
                Please adjust the selected questions or filters.
              </p>
            </div>
          </template>
          <template v-else>
            <chartGrid
              :columns="1"
              :only="['graphChart']"
              :class="`clip-required ${!showGraph && 'hidden'}`"
              :aria-hidden="!showGraph"
            />
            <chartGrid
              :columns="1"
              :only="['tableChart']"
              :class="`clip-required ${showGraph && 'hidden'}`"
              :aria-hidden="showGraph"
            />
            <div
              v-if="chartData != null && chartData.data.length > 0"
              id="dashboard-footnotes-container"
              class="table-footnotes clip-required"
            >
              <p class="table-footnotes-header">
                Notes:
              </p>
              <div
                v-if="chartData.footnotes.benchmark.hasBenchmark"
                class="table-footnotes-item"
              >
                <span class="table-footnotes-benchmark-symbol">{{ chartData.footnotes.benchmark.symbol }}</span> {{ chartData.footnotes.benchmark.text }}
              </div>
              <div
                v-if="chartData.footnotes.subpops"
                class="table-footnotes-item"
              >
                <p>
                  {{ chartData.footnotes.subpops.text }}
                </p>
                <ul v-if="chartData.footnotes.subpops && chartData.footnotes.subpops.items">
                  <li
                    v-for="subpop in chartData.footnotes.subpops.items"
                    :key="subpop.id"
                  >
                    {{ subpop.text }}
                  </li>
                </ul>
              </div>
              <div
                v-if="chartData.footnotes.suppressed"
                class="table-footnotes-item"
              >
                <span class="table-footnotes-suppressed-symbol">{{ chartData.footnotes.suppressed.symbol }}</span> {{ chartData.footnotes.suppressed.text }}
              </div>
              <div class="table-footnotes-item">
                {{ chartData.footnotes.variableName }}
              </div>
              <div class="table-footnotes-item">
                {{ chartData.footnotes.weighting }}
              </div>
              <div class="table-footnotes-item">
                {{ chartData.footnotes.compare2014 }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Hs } from '@rtidatascience/harness'
import SidebarComponent from './Sidebar.vue'
import { mapGetters, mapState } from 'vuex'
import { saveAs } from 'file-saver'
import { prettyPrintPopulationSize, prettyPrintProportion, prettyPrintSampleSize, safeStringForCSV } from '../utils'
import webFonts from '../webFonts'

export default {
  components: { SidebarComponent },
  name: 'TopicPage',
  computed: {
    ...mapState(['isLoadingYear']),
    ...mapGetters(['rowQuestion', 'columnQuestion', 'filterQuestion', 'filteredRowQuestion', 'filteredColumnQuestion']),
    dataYear: {
      get () {
        return this.$store.state.selectedYear
      },
      set (value) {
        this.$store.commit('clearRowQuestion')
        this.$store.commit('clearColumnQuestion')
        this.$store.commit('clearFilterQuestion')
        this.$store.commit('setYear', value)
        this.$store.commit('setIsLoadingYear', true)

        setTimeout(() => {
          const hs = new Hs('topic', this.$store)
          hs.loadData()
        }, 100)
      }
    },
    selectedQuestion () {
      const hs = new Hs('topic', this.$store)
      return hs.getFilter('accordion')
    },
    chartData () {
      const hs = new Hs('topic', this.$store)
      return hs.getChartData('graphChart')
    },
    showGraph () {
      const hs = new Hs('topic', this.$store)
      return hs.getFilter('showGraph')
    }
  },
  methods: {
    exportToPNG () {
      const ns = 'http://www.w3.org/2000/svg'

      const svgElement = document.querySelector('#dashboard-chart-container svg').cloneNode(true)
      svgElement.setAttribute('aria-hidden', true)
      document.getElementById('dashboard-chart-container').appendChild(svgElement)

      const { width: chartWidth, height: chartHeight } = svgElement.getBoundingClientRect()

      const existingFootnotesElement = document.getElementById('dashboard-footnotes-container')
      // I found that using the exact height tended to cut off the bottom line,
      // so add some extra pixels just to be safe.
      const footnotesHeight = existingFootnotesElement.getBoundingClientRect().height + 50
      const combinedHeight = chartHeight + footnotesHeight

      svgElement.setAttribute('height', combinedHeight)
      svgElement.setAttribute('viewbox', `0 0 ${chartWidth} ${combinedHeight}`)

      const foreignElement = document.createElementNS(ns, 'foreignObject')
      foreignElement.setAttribute('width', chartWidth)
      foreignElement.setAttribute('height', footnotesHeight)
      foreignElement.setAttribute('x', 0)
      foreignElement.setAttribute('y', chartHeight)

      const styleElement = document.createElementNS(ns, 'style')
      styleElement.append(`
        ${webFonts}

        .table-footnotes {
          font-family: Source Sans Pro, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #323a45;
        }
        .table-footnotes-header {
          font-weight: bold;
          margin-bottom: 1em;
        }
        .table-footnotes-item {
          margin-bottom: 1em;
        }
        .table-footnotes-item p {
          margin-bottom: 0.5em;
        }
        .table-footnotes-suppressed-symbol {
          font-family: Merriweather, serif;
        }
        .table-footnotes-benchmark-symbol {
          font-family: Roboto Mono Dagger, sans-serif;
        }
      `)
      svgElement.prepend(styleElement)

      const footnotesElement = existingFootnotesElement.cloneNode(true)
      foreignElement.append(footnotesElement)
      svgElement.append(foreignElement)

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      // Add a few pixels to the width and height so that we don't run the svg
      // right up to the edge of the canvas.
      const canvasMargin = 4
      canvas.width = chartWidth + (2 * canvasMargin)
      canvas.height = combinedHeight + (2 * canvasMargin)

      const xml = (new XMLSerializer()).serializeToString(svgElement)

      const image = new Image()
      image.onload = () => {
        context.fillStyle = 'white'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, canvasMargin, canvasMargin)
        canvas.toBlob(blob => {
          saveAs(blob, 'hcps_export_chart.png')
        })
      }
      image.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)))

      svgElement.remove()
    },
    exportToCSV () {
      const hs = new Hs('topic', this.$store)
      const data = hs.getChartData('tableChart')
      const table = this.buildExport(
        data.table,
        data.footnotes.suppressed.symbol,
        data.footnotes.benchmark.hasBenchmark
      )

      const output = [
        [safeStringForCSV(data.title)],
        [safeStringForCSV(data.subtitle)],
        [''],
        ...table,
        [''],
        ['Notes:'],
        ...this.buildFooter(data.footnotes)
      ]

      const csv = output
        .map(r => r.join(','))
        .join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      saveAs(blob, 'hcps_export_table.csv')
    },
    buildExport (data, suppressedSymbol, hasBenchmark) {
      const majorId = this.rowQuestion.id
      const minorId = this.columnQuestion?.id
      const showCI = this.getFilter('confidenceInterval')
      const include = [
        true,
        minorId != null,
        true,
        showCI,
        showCI,
        hasBenchmark,
        true,
        true,
        showCI,
        showCI
      ]
      const head = [
        `[${majorId}]`,
        `[${this.columnQuestion ? minorId : null}]`,
        minorId == null ? 'Percentage' : `Percentage within [${majorId}] category`,
        'Lower bound of 95% confidence interval for percentage',
        'Upper bound of 95% confidence interval for percentage',
        'CG-CAHPS benchmark for percentage',
        'Sample Size',
        'Population size',
        'Lower bound of 95% confidence interval for population size',
        'Upper bound of 95% confidence interval for population size'
      ]

      const map = data.flatMap((d, i) => {
        return d.map((dd, ddi) => {
          return this.compressArray(
            [
              safeStringForCSV(this.filteredRowQuestion.levels[i].label),
              this.filteredColumnQuestion && safeStringForCSV(this.filteredColumnQuestion?.levels[ddi].label),
              dd.suppressed ? suppressedSymbol : prettyPrintProportion(dd.proportion),
              dd.suppressed ? suppressedSymbol : prettyPrintProportion(dd.ciProportion.lower),
              dd.suppressed ? suppressedSymbol : prettyPrintProportion(dd.ciProportion.upper),
              hasBenchmark && dd.benchmark != null ? dd.benchmark : 'n/a',
              dd.suppressed ? suppressedSymbol : safeStringForCSV(prettyPrintSampleSize(dd.sampleSize)),
              dd.suppressed ? suppressedSymbol : safeStringForCSV(prettyPrintPopulationSize(dd.numerator)),
              dd.suppressed ? suppressedSymbol : safeStringForCSV(prettyPrintPopulationSize(dd.ciNumerator.lower)),
              dd.suppressed ? suppressedSymbol : safeStringForCSV(prettyPrintPopulationSize(dd.ciNumerator.upper))
            ],
            include
          )
        })
      })

      const out = [this.compressArray(head, include), ...map]
      return out
    },
    compressArray (data, include) {
      return data.reduce((out, d, i) => {
        if (include[i]) {
          out.push(d)
        }

        return out
      }, [])
    },
    buildFooter (footnotes) {
      if (footnotes == null) {
        return []
      }

      const { weighting, subpops, suppressed, variableName, benchmark, compare2014 } = footnotes
      const parts = []

      benchmark.hasBenchmark && parts.push([safeStringForCSV(`${benchmark.symbol} ${benchmark.text}`)])

      if (subpops) {
        parts.push([safeStringForCSV(subpops.text)])
        subpops.items.forEach(i => i && parts.push([safeStringForCSV(i.text)]))
      }

      suppressed && parts.push([safeStringForCSV(`${suppressed.symbol} ${suppressed.text}`)])
      variableName && parts.push([safeStringForCSV(variableName)])

      parts.push([safeStringForCSV(weighting)])
      parts.push([safeStringForCSV(compare2014)])

      return parts
    },
    isChartNoResults () {
      const data = this.chartData.data

      return !!data
        .every(d => d.every(r => r.noResult))
    }
  }
}
</script>

<style lang="scss">
/* Unscoped because these apply to elements generated by HarnessUI. */
.topbar-option .col-form-label {
    font-weight: bold;
    padding-bottom: 0;
}
.topbar-option .form-check {
    margin-left: 7px;
}

.hcps-parameters {
  .harness-ui-filtergrid-row {
    .harness-ui-radiogroup-legend {
      border-bottom: 0;
      color: #323a45;
    }

    .form-check-input {
      margin-left: -1.25rem;
    }

    .harness-ui-radiogroup-label {
      margin-left: 0.5rem;
    }
  }

  /* The HarnessUI default would pass a 508 contrast audit on a white background,
   * but our background isn't white, so make the color darker.
   */
  .form-check-input[disabled] ~ .form-check-label,
  .form-check-input:disabled ~ .form-check-label {
    color: #666f77 !important;
  }
}
</style>

<style lang="scss" scoped>
.container {
    width: 100%;
    height: 100%;
    background-color: #fff;

    padding: 0px;

    .options {
        width: 100%;
        height: 100%;
        display: flex;

        .topics {
            flex: 0 0 300px;
        }

        .hcps-parameters {
            flex: 1 1 auto;
            padding-left: 16px;
            display: flex;
            flex-direction: column;
            overflow-x: auto;

            .topbar {
              background-color: #edf1f7;
              padding: 12px;
              padding-top: 4px;
              margin-bottom: 24px;
            }
        }

        @media screen and (max-width: 768px)  {
          & {
            flex-direction: column;

            .topics, .hcps-parameters {
              flex: 0 0 100%;
            }

            .hcps-parameters {
              padding-left: 0px;
            }
          }
        }
    }

    .clip-required {
      overflow-x: clip;
    }

    /* Be sure to copy any footnote styles to the exportToPng function. */
    .table-footnotes-header {
      font-weight: bold;
      margin-bottom: 1em;
    }
    .table-footnotes-item {
      margin-bottom: 1em;
    }
    .table-footnotes-item p {
      margin-bottom: 0.5em;
    }
    .table-footnotes-suppressed-symbol {
      font-family: Merriweather, serif;
    }
    .table-footnotes-benchmark-symbol {
      font-family: Roboto Mono Dagger, sans-serif;
    }
}

.topbar-option-value {
    margin-left: 7px;
}

.download-button {
    padding: 0;
    border: 0;
    background: none;
    transition: 300ms ease all;

    .badge {
        background-color: #267cb8;
        transition: 300ms ease all;
    }

    &:hover {
        color: #267cb8;

        .badge {
            background-color: #205492;
        }
    }
}

.alert-message {
  width: 60%;
  margin: 80px auto;
  padding: 8px;
  font-size: 16px;
  text-align: center;

  p:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 576px) {
    & {
      width: 100%;
    }
  }
}

.hidden {
  height: 1px;
  overflow: hidden;
}

.year-banner {
    padding: 8px;
    margin-bottom: 24px;
    background-color: #267cb8;
    color: #fff;
    text-align: center;

    p {
        margin: 0;
    }

    label {
        margin-right: 0.5rem;
        font-family: Merriweather;
        font-weight: bold;
        font-size: 1.25rem;
    }

  .hcps-year-select {
    color: #000;
  }
}
</style>
